import type { DynamoDbCarDetails } from '@ourbranch/schema-types/dynamodb';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState, formatAddress, getPrimarySegment, normalizeAddress } from './lib/helpers';

export const garageMailingAddressMismatch = defineInternalTask({
  taskName: internalTasks.GarageMailingAddressMismatch,
  config: {
    staffDisplayName: 'Mismatching mailing/garaging addresses: verification needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['A'],
    enabled: true,
    // Legacy source: `dynamo-be/lambdas/bind-requests-to-accounts/src/repositories/ticket.repository.js:220-270`.
    // Legacy `policyDetails = policy.segments[0]` (same file, line 73), so all reads
    // below use `policy.segments[0]` to match the legacy node exactly. The mailing
    // address is copied from `bindDetails` onto `offer.quote.global.mailingAddress`
    // at bind time (see `bind-requests-to-accounts/src/index.js:613`), and then to
    // the bound policy at `policy.segments[0].global.mailingAddress`.
    trigger: ({ policy }) => {
      const segment = getPrimarySegment(policy);
      // Filter with a type predicate so downstream code sees `DynamoDbCarDetails` (not
      // `Maybe<CarDetails>`) — `.filter(Boolean)` doesn't narrow nullable types, and
      // narrowing to the DDB-extension type is needed to access fields like
      // `isGarageAddressRuleExceptionApproved` that only live on DynamoDbCarDetails.
      const cars = (segment?.cars || []).filter((c): c is DynamoDbCarDetails => !!c);
      const correctedAddress = segment?.correctedAddress;
      if (!cars.length || !correctedAddress) {
        return null;
      }

      const mailingAddress = segment?.global?.mailingAddress;

      // Compare full address (street + city + state + zip) — same-street different-city
      // addresses must not compare equal.
      const policyAddr = normalizeAddress(correctedAddress);
      const mailAddr = normalizeAddress(mailingAddress || undefined);

      const mismatchedCars: string[] = [];
      for (const car of cars) {
        if (car.isGarageAddressRuleExceptionApproved) {
          continue;
        }
        const garageAddr = normalizeAddress(car.garageLocation || undefined);
        if (garageAddr && garageAddr !== policyAddr) {
          mismatchedCars.push(
            `${car.make || ''} ${car.model || ''} ${car.year || ''} - Garage: ${formatAddress(car.garageLocation || undefined)}`
          );
        }
      }

      const mailingMismatch = !!(mailAddr && policyAddr && mailAddr !== policyAddr);
      if (mismatchedCars.length === 0 && !mailingMismatch) {
        return null;
      }

      return {
        data: {
          homeAddress: formatAddress(correctedAddress),
          mailingAddress: formatAddress(mailingAddress || undefined),
          vehicleAddresses: mismatchedCars
        },
        bindState: baseBindState(policy)
      };
    },
    description: ({ data, bindState }) => {
      const lines = [
        'Verify addresses match',
        `Home Address: ${data.homeAddress}`,
        `Mailing Address: ${data.mailingAddress}`,
        '',
        `Policy state: ${bindState.state}`
      ];
      if (data.vehicleAddresses?.length) {
        lines.push('', 'Please verify the addresses for the vehicles below:', ...data.vehicleAddresses);
      }
      return lines.join('\n');
    }
  }
});
